import React, { CSSProperties } from "react";
import classNames from "classnames";
import { Drum, MAX_BPM, MIN_BPM, TR808State } from "./core";
import { StepSequencer } from "./StepSequencer";
import { Knob } from "./Knob";
import EventListener from "react-event-listener";

import * as tr808 from "./audio/tr808";
import * as sfx from "./audio/sfx";

import "./TR808.scss";

import TR808Branding from "./assets/images/TR-808.svg";
import TR808BrandingMobile from "./assets/images/TR-808--mobile.svg";

import columnLabel1 from "./assets/images/808ColumnLabel--1.svg";
import columnLabel2 from "./assets/images/808ColumnLabel--2.svg";
import columnLabel3 from "./assets/images/808ColumnLabel--3.svg";
import columnLabel4 from "./assets/images/808ColumnLabel--4.svg";
import columnLabel5 from "./assets/images/808ColumnLabel--5.svg";
import columnLabel6 from "./assets/images/808ColumnLabel--6.svg";
import columnLabel7 from "./assets/images/808ColumnLabel--7.svg";
import columnLabel8 from "./assets/images/808ColumnLabel--8.svg";
import columnLabel9 from "./assets/images/808ColumnLabel--9.svg";
import columnLabel10 from "./assets/images/808ColumnLabel--10.svg";
import columnLabel11 from "./assets/images/808ColumnLabel--11.svg";
import columnLabel12 from "./assets/images/808ColumnLabel--12.svg";
import columnLabel13 from "./assets/images/808ColumnLabel--13.svg";
import columnLabel14 from "./assets/images/808ColumnLabel--14.svg";
import columnLabel15 from "./assets/images/808ColumnLabel--15.svg";
import columnLabel16 from "./assets/images/808ColumnLabel--16.svg";

import rowLabelCH from "./assets/images/808RowLabel--CH.svg";
import rowLabelOH from "./assets/images/808RowLabel--OH.svg";
import rowLabelCY from "./assets/images/808RowLabel--CY.svg";
import rowLabelCB from "./assets/images/808RowLabel--CB.svg";
import rowLabelCP from "./assets/images/808RowLabel--CP.svg";
import rowLabelRS from "./assets/images/808RowLabel--RS.svg";
import rowLabelHT from "./assets/images/808RowLabel--HT.svg";
import rowLabelMT from "./assets/images/808RowLabel--MT.svg";
import rowLabelLT from "./assets/images/808RowLabel--LT.svg";
import rowLabelSD from "./assets/images/808RowLabel--SD.svg";
import rowLabelBD from "./assets/images/808RowLabel--BD.svg";
import rowLabelAC from "./assets/images/808RowLabel--AC.svg";

import buttonLabelAiVariation from "./assets/images/808SwitchLabel--aiVariation.svg";
import buttonToggle from "./assets/images/303Button--toggle.svg";

import knobForegroundTempo from "./assets/images/808Knob--tempo.svg";
import knobForegroundShuffle from "./assets/images/808Knob--shuffle.svg";
import knobBackground from "./assets/images/808KnobBackground.svg";
import knobLabelShuffle from "./assets/images/808KnobLabel--shuffle.svg";
import knobLabelTempo from "./assets/images/808KnobLabel--tempo.svg";

const ROW_HEADER_IMAGES: { [drum: string]: string } = {
  CH: rowLabelCH,
  OH: rowLabelOH,
  CY: rowLabelCY,
  CB: rowLabelCB,
  CP: rowLabelCP,
  RS: rowLabelRS,
  HT: rowLabelHT,
  MT: rowLabelMT,
  LT: rowLabelLT,
  SD: rowLabelSD,
  BD: rowLabelBD,
  AC: rowLabelAC,
};

interface TR808Props {
  state: TR808State;
  bpm: number;
  isKeyboardControlsActive?: boolean;
  onStart: () => void;
  onStop: () => void;
  onSetAiVariationOn: (on: boolean) => void;
  onToggleHit: (timeStep: number, column: Drum, isFillHit: boolean) => void;
  onClearHits: () => void;
  onSetBpm: (newBpm: number) => void;
  onSetShuffle: (newShuffle: number) => void;
  onTriggerFill: () => void;
  onSwitchDevice: () => void;
}
export const TR808: React.FC<TR808Props> = React.memo(
  ({
    state,
    bpm,
    isKeyboardControlsActive,
    onStart,
    onStop,
    onSetAiVariationOn,
    onToggleHit,
    onClearHits,
    onSetBpm,
    onSetShuffle,
    onTriggerFill,
    onSwitchDevice,
  }) => {
    return (
      <div
        className={classNames("tr808", `steps-${state.layout.timeSteps}`)}
        style={{ "--numTimeSteps": state.layout.timeSteps } as CSSProperties}
      >
        <svg
          className="logo"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 157.08 24.15"
        >
          <path d="M90.93.94V22.52a.93.93,0,0,0,.94.94h2.57a.93.93,0,0,0,.94-.94V.94A.92.92,0,0,0,94.44,0H91.87A.92.92,0,0,0,90.93.94Z" />
          <path d="M135.4,22.52V13a6.93,6.93,0,0,0-6.48-6.87,10.55,10.55,0,0,0-6.81,2.15c-.16.13-.6.26-.6-.13v-1a.91.91,0,0,0-.93-.93h-2.22a.92.92,0,0,0-.94.93V22.52a.92.92,0,0,0,.94.94h2.22a.92.92,0,0,0,.93-.94V15a5.6,5.6,0,0,1,6-5.61c3.28,0,3.8,2.26,3.8,3.6v9.51a.91.91,0,0,0,.94.93h2.21A.92.92,0,0,0,135.4,22.52Z" />
          <path d="M97.83,11v.1" />
          <path d="M147.1,9.5a5.52,5.52,0,0,1,5.79,5.61,5.72,5.72,0,1,1-11.43,0A5.55,5.55,0,0,1,147.1,9.5ZM152.45,22c.27-.21.55,0,.54.46a.94.94,0,0,0,.94,1h2.22a.91.91,0,0,0,.93-.94V.94a.9.9,0,0,0-.93-.93h-2.22a.91.91,0,0,0-.94.93V7.75c0,.5-.27.67-.54.46-6-4.63-15.22,0-15.2,6.85C137.27,22.72,146.32,26.85,152.45,22Z" />
          <path d="M102.32,16.77c2.12-1.12,7.34-1,7.82-.95.25,0,.6.21.59.55,0,4.52-6.18,5.13-8.54,3.57a1.86,1.86,0,0,1-.83-1.43A1.75,1.75,0,0,1,102.32,16.77Zm-2.75,6.07c2.9,2.07,8.29,1.57,10.74-.58a.45.45,0,0,1,.74.26c.24.75.59.93,1.34.94h1.53a.91.91,0,0,0,.93-.94V12.81c0-5.28-4.92-6.68-8.7-6.68-4.22,0-7,1.61-8.32,4.87-.14.33.26.73.59.73h2.88a1.59,1.59,0,0,0,1.14-.66c.74-1.26,2-1.72,4.23-1.72,2.51,0,3.17.56,4,1.72a1,1,0,0,1-.58,1.54,61,61,0,0,0-8.73.8,5.15,5.15,0,0,0-1.78,9.42ZM78.86,24.06c7.48,0,10.61-4.85,10.61-9,0-3.93-3.15-9-10.67-9s-10.65,5-10.65,9C68.15,19.29,71.32,24.06,78.86,24.06Zm0-3.21c-4.37,0-6.37-3.24-6.37-5.72a6.35,6.35,0,0,1,12.64,0A6,6,0,0,1,78.86,20.85Zm-28.67-7v8.67a.91.91,0,0,1-.94.93H47.16a.91.91,0,0,1-.95-.93V.93A.92.92,0,0,1,47.15,0h13.6a6.37,6.37,0,0,1,6.59,6.45,6.55,6.55,0,0,1-2.9,5.24c-.14.09-.24.27-.18.32,1.17.92,1.18,1.66,1.59,3.23.73,2.84.82,5.32,1.94,7.7a.42.42,0,0,1-.37.5H64.71a1.45,1.45,0,0,1-1.54-.94c-1-3-.31-5.85-2.33-8.67a3.35,3.35,0,0,0-2.76-.94H51.13A.92.92,0,0,0,50.19,13.84Zm9.24-4.05s3.65,0,3.65-3.34S59.43,3.1,59.43,3.1h-8.3a.92.92,0,0,0-.94.94V8.85a1,1,0,0,0,.94.94Z" />
          <path d="M37.09,10.29h-23A5.67,5.67,0,0,0,8.32,16a5.81,5.81,0,0,0,5.8,5.75H42.21v1.7H14.09a7.44,7.44,0,1,1,0-14.87H35.32V6.25H6.53V23.44H4.64V4.53H37.09Z" />
          <path d="M12.36,18.93H42.21V17.22H14.12V14.89H35.37a7.35,7.35,0,0,0,7.55-7.43A7.46,7.46,0,0,0,35.26,0H0V23.44H1.89V1.72H35.32a5.83,5.83,0,0,1,5.82,5.76,5.68,5.68,0,0,1-5.82,5.69h-23Z" />
        </svg>
        <StepSequencer<Drum>
          timeSteps={state.layout.timeSteps}
          rows={state.layout.rows}
          rowHeaderImages={ROW_HEADER_IMAGES}
          currentHits={
            state.aiVariationOn ? state.aiVariationSequence : state.sequence
          }
          currentAltHits={state.fillSequence}
          marchProvider={tr808.subscribeToMarch}
          onToggleHit={onToggleHit}
        />
        <img
          className="branding"
          src={TR808Branding}
          alt="Rhythm Composer, computer controlled. TR-808"
        />
        <img
          className="branding--mobile"
          src={TR808BrandingMobile}
          alt="Rhythm Composer. TR-808"
        />
        <div className="controls">
          <div className="columnLabels">
            <div className="columnProp">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16.24 10">
                <rect fill="none" x="0" y="0" width="16.24" height="10" />
              </svg>
            </div>
            <div className="columnLabel">
              <img src={columnLabel1} alt="1" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel2} alt="2" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel3} alt="3" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel4} alt="4" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel5} alt="5" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel6} alt="6" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel7} alt="7" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel8} alt="8" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel9} alt="9" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel10} alt="10" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel11} alt="11" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel12} alt="12" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel13} alt="13" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel14} alt="14" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel15} alt="15" />
            </div>
            <div className="columnLabel">
              <img src={columnLabel16} alt="16" />
            </div>
          </div>
          <div className="switches">
            <div className="aiVariationStartStop">
              <div className="switch switch--aiVariation">
                <img
                  className="buttonLabel buttonLabel--aiVariation"
                  src={buttonLabelAiVariation}
                  alt="AI Variation"
                />
                <label className="aiVariationToggle">
                  <span className="sr-only">AI-variation</span>
                  <input
                    className="offscreen"
                    type="checkbox"
                    checked={state.aiVariationOn}
                    onChange={(evt) => {
                      onSetAiVariationOn(evt.target.checked);
                      sfx.triggerTR808AISwitch();
                    }}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 80 75.59"
                  >
                    <rect
                      className="toggleBackground"
                      width="80"
                      height="24.24"
                      rx="12.12"
                    />
                    <circle
                      className="toggle toggle--on"
                      cx="12.12"
                      cy="12.12"
                      r="9.68"
                    />
                    <circle
                      className="toggle toggle--off"
                      cx="67.88"
                      cy="12.12"
                      r="9.68"
                    />
                    <rect
                      className="lightBackground"
                      y="51.35"
                      width="80"
                      height="24.24"
                    />
                    <ellipse
                      className="light light--on"
                      cx="16.66"
                      cy="63.47"
                      rx="3.84"
                      ry="4.03"
                    />
                    <ellipse
                      className="light light--off"
                      cx="63.34"
                      cy="63.47"
                      rx="3.84"
                      ry="4.03"
                    />
                    <path
                      className="text text--on"
                      d="M12.38,41.39a3.13,3.13,0,0,1-2.36.84,3.13,3.13,0,0,1-2.35-.84,3.86,3.86,0,0,1-1.1-3,3.83,3.83,0,0,1,1.1-3A3.16,3.16,0,0,1,10,34.61a3.17,3.17,0,0,1,2.36.84,3.83,3.83,0,0,1,1.09,3A3.86,3.86,0,0,1,12.38,41.39Zm-.94-1.09a3.58,3.58,0,0,0,0-3.75A1.71,1.71,0,0,0,10,35.89a1.73,1.73,0,0,0-1.42.66,2.87,2.87,0,0,0-.53,1.87A2.9,2.9,0,0,0,8.6,40.3,1.73,1.73,0,0,0,10,41,1.71,1.71,0,0,0,11.44,40.3Z"
                    />
                    <path
                      className="text text--on"
                      d="M14.59,34.83h1.58l2.86,5v-5h1.4V42h-1.5L16,36.93V42H14.59Z"
                    />
                    <path
                      className="text text--off"
                      d="M59.65,41.39a3.74,3.74,0,0,1-4.72,0,3.86,3.86,0,0,1-1.09-3,3.83,3.83,0,0,1,1.09-3,3.74,3.74,0,0,1,4.72,0,3.87,3.87,0,0,1,1.09,3A3.9,3.9,0,0,1,59.65,41.39Zm-.94-1.09a3.64,3.64,0,0,0,0-3.75,1.73,1.73,0,0,0-1.42-.66,1.75,1.75,0,0,0-1.43.66,2.93,2.93,0,0,0-.53,1.87,3,3,0,0,0,.53,1.88,1.75,1.75,0,0,0,1.43.66A1.73,1.73,0,0,0,58.71,40.3Z"
                    />
                    <path
                      className="text text--off"
                      d="M61.88,34.84H67v1.27H63.38v1.65h3.19V39H63.38v3h-1.5Z"
                    />
                    <path
                      className="text text--off"
                      d="M68,34.84h5.13v1.27H69.49v1.65h3.18V39H69.49v3H68Z"
                    />
                  </svg>
                </label>
              </div>
              <div className="switch switch--fill">
                <button
                  className="button button--fill button--yellow"
                  onClick={() => {
                    onTriggerFill();
                    sfx.triggerTR808FillIn();
                  }}
                >
                  <span className="sr-only">Fill</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 64.64 64.64"
                  >
                    <rect
                      className="background"
                      x="2"
                      y="2"
                      width="60.64"
                      height="60.64"
                    />
                    <path
                      className="border"
                      d="M60.64,4V60.64H4V4H60.64m4-4H0V64.64H64.64V0Z"
                    />
                    <path
                      className="text"
                      d="M22.46,27.72h6v1.05H23.62v2.61h4.23v1H23.62v3.91H22.46Z"
                    />
                    <path className="text" d="M29.34,27.72h1.18v8.6H29.34Z" />
                    <path
                      className="text"
                      d="M31.81,27.72H33V35.3h4.31v1H31.81Z"
                    />
                    <path
                      className="text"
                      d="M37.88,27.72h1.17V35.3h4.32v1H37.88Z"
                    />
                  </svg>
                </button>
              </div>
              <div className="switch switch--startStop">
                <div className="toggleStartStop">
                  <button
                    className={classNames(
                      "button button--start button--yellow",
                      {
                        isVisible: !state.playing,
                      }
                    )}
                    onClick={(evt) => {
                      onStart();
                      sfx.triggerTR808StartStop();
                      evt.currentTarget.blur();
                    }}
                  >
                    <span className="sr-only">Start</span>
                    <svg
                      className="button--startImage"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 154.06 64.64"
                    >
                      <rect
                        className="background"
                        x="2"
                        y="2"
                        width="150.06"
                        height="60.64"
                      />
                      <path
                        className="border"
                        d="M150.06,4V60.64H4V4H150.06m4-4H0V64.64H154.06V0Z"
                      />
                      <path
                        className="text"
                        d="M61.07,33.83A2.35,2.35,0,0,0,61.4,35a2.17,2.17,0,0,0,2,.85,3.23,3.23,0,0,0,1.15-.19,1.32,1.32,0,0,0,1-1.3,1.15,1.15,0,0,0-.43-1,4.54,4.54,0,0,0-1.35-.51l-1.13-.26A4.93,4.93,0,0,1,61.05,32a1.82,1.82,0,0,1-.79-1.61A2.52,2.52,0,0,1,61,28.52a3.11,3.11,0,0,1,2.21-.74,3.8,3.8,0,0,1,2.24.65,2.4,2.4,0,0,1,.92,2.09H65.32A2.31,2.31,0,0,0,65,29.46a2.06,2.06,0,0,0-1.74-.67,2,2,0,0,0-1.42.43,1.39,1.39,0,0,0-.43,1,1,1,0,0,0,.51.92,6.57,6.57,0,0,0,1.51.47l1.17.28a3.49,3.49,0,0,1,1.31.54,2.08,2.08,0,0,1,.79,1.76,2.21,2.21,0,0,1-1,2,4.47,4.47,0,0,1-2.36.62A3.5,3.5,0,0,1,60.84,36,2.82,2.82,0,0,1,60,33.83Z"
                      />
                      <path
                        className="text"
                        d="M74,28v1h-2.9V36.6H69.89V29H67V28Z"
                      />
                      <path
                        className="text"
                        d="M76.06,28h1.31l3.13,8.6H79.22L78.35,34H74.94L74,36.6H72.82ZM78,33.08l-1.3-3.81-1.39,3.81Z"
                      />
                      <path
                        className="text"
                        d="M81.1,28H85a3.89,3.89,0,0,1,1.6.28,2.05,2.05,0,0,1,1.19,2,2.11,2.11,0,0,1-1.22,2,1.79,1.79,0,0,1,.76.54A2,2,0,0,1,87.63,34l0,1.15a3.62,3.62,0,0,0,.08.73.74.74,0,0,0,.37.53v.19H86.7a.91.91,0,0,1-.1-.28c0-.12,0-.35-.06-.68l-.07-1.44a1.2,1.2,0,0,0-.61-1.13,2.5,2.5,0,0,0-1-.16H82.27V36.6H81.1Zm3.79,3.94a2.14,2.14,0,0,0,1.26-.33,1.35,1.35,0,0,0,.46-1.18A1.28,1.28,0,0,0,86,29.17,2.18,2.18,0,0,0,85,29H82.27v2.94Z"
                      />
                      <path
                        className="text"
                        d="M95.07,28v1h-2.9V36.6H91V29h-2.9V28Z"
                      />
                    </svg>
                    <svg
                      className="button--startImageMobile"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 128 64.64"
                    >
                      <rect
                        className="background"
                        x="2"
                        y="2"
                        width="124"
                        height="60.64"
                      />
                      <path
                        className="border"
                        d="M124,4V60.64H4V4H124m4-4H0V64.64H128V0Z"
                      />
                      <path
                        className="text"
                        d="M48,33.83A2.46,2.46,0,0,0,48.37,35a2.17,2.17,0,0,0,2,.85,3.23,3.23,0,0,0,1.15-.19,1.32,1.32,0,0,0,1-1.3,1.12,1.12,0,0,0-.43-1,4.54,4.54,0,0,0-1.35-.51l-1.13-.26A4.93,4.93,0,0,1,48,32a1.82,1.82,0,0,1-.79-1.61A2.52,2.52,0,0,1,48,28.52a3.11,3.11,0,0,1,2.21-.74,3.8,3.8,0,0,1,2.24.65,2.37,2.37,0,0,1,.92,2.09H52.29a2.31,2.31,0,0,0-.36-1.06,2,2,0,0,0-1.74-.67,2,2,0,0,0-1.42.43,1.39,1.39,0,0,0-.43,1,1,1,0,0,0,.51.92,6.57,6.57,0,0,0,1.51.47l1.17.28a3.49,3.49,0,0,1,1.31.54,2.08,2.08,0,0,1,.79,1.76,2.19,2.19,0,0,1-1,2,4.47,4.47,0,0,1-2.36.62A3.5,3.5,0,0,1,47.81,36a2.82,2.82,0,0,1-.87-2.21Z"
                      />
                      <path
                        className="text"
                        d="M60.94,28v1H58V36.6H56.86V29H54V28Z"
                      />
                      <path
                        className="text"
                        d="M63,28h1.31l3.13,8.6H66.2L65.3,34H61.93L61,36.6H59.8ZM65,33.08l-1.32-3.81-1.38,3.81Z"
                      />
                      <path
                        className="text"
                        d="M68.07,28H72a3.89,3.89,0,0,1,1.6.28,2.05,2.05,0,0,1,1.19,2,2.31,2.31,0,0,1-.32,1.26,2.29,2.29,0,0,1-.89.79,1.82,1.82,0,0,1,.75.54A2,2,0,0,1,74.6,34l0,1.15a3.62,3.62,0,0,0,.08.73.72.72,0,0,0,.38.53v.19H73.67a1.28,1.28,0,0,1-.1-.28c0-.12,0-.35-.06-.68l-.07-1.44a1.2,1.2,0,0,0-.61-1.13,2.48,2.48,0,0,0-1-.16H69.24V36.6H68.07Zm3.79,3.94a2.14,2.14,0,0,0,1.26-.33,1.35,1.35,0,0,0,.46-1.18,1.29,1.29,0,0,0-.64-1.26A2.24,2.24,0,0,0,72,29H69.24v2.94Z"
                      />
                      <path
                        className="text"
                        d="M82,28v1h-2.9V36.6H78V29h-2.9V28Z"
                      />
                    </svg>
                  </button>
                  <button
                    className={classNames(
                      "button button--stop button--yellow",
                      {
                        isVisible: state.playing,
                      }
                    )}
                    onClick={(evt) => {
                      onStop();
                      sfx.triggerTR808StartStop();
                      evt.currentTarget.blur();
                    }}
                  >
                    <span className="sr-only">Stop</span>
                    <svg
                      className="button--stopImage"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 154.06 64.64"
                    >
                      <rect
                        className="background"
                        x="2"
                        y="2"
                        width="150.06"
                        height="60.64"
                      />
                      <path
                        className="border"
                        d="M150.06,4V60.64H4V4H150.06m4-4H0V64.64H154.06V0Z"
                      />
                      <path
                        className="text"
                        d="M63.66,33.84A2.28,2.28,0,0,0,64,35a2.15,2.15,0,0,0,2,.86,3.27,3.27,0,0,0,1.15-.19,1.31,1.31,0,0,0,1-1.3,1.14,1.14,0,0,0-.42-1,4,4,0,0,0-1.35-.51l-1.13-.26a5.18,5.18,0,0,1-1.57-.57,1.82,1.82,0,0,1-.79-1.61,2.52,2.52,0,0,1,.78-1.91,3.11,3.11,0,0,1,2.21-.74,3.82,3.82,0,0,1,2.24.65A2.4,2.4,0,0,1,69,30.53H67.9a2.1,2.1,0,0,0-.36-1.06,2,2,0,0,0-1.73-.67,2,2,0,0,0-1.42.43,1.38,1.38,0,0,0-.44,1,1,1,0,0,0,.52.92,6.93,6.93,0,0,0,1.51.47l1.17.28a3.56,3.56,0,0,1,1.31.54,2.06,2.06,0,0,1,.79,1.75,2.21,2.21,0,0,1-1,2,4.46,4.46,0,0,1-2.36.61A3.46,3.46,0,0,1,63.43,36a2.79,2.79,0,0,1-.87-2.2Z"
                      />
                      <path
                        className="text"
                        d="M76.56,28v1h-2.9v7.58H72.48V29h-2.9V28Z"
                      />
                      <path
                        className="text"
                        d="M83.91,29.24a4.73,4.73,0,0,1,.85,2.92,5.06,5.06,0,0,1-1,3.2,4.34,4.34,0,0,1-6.39.19,4.93,4.93,0,0,1-1-3.2,5.09,5.09,0,0,1,.87-3,3.76,3.76,0,0,1,3.29-1.59A3.94,3.94,0,0,1,83.91,29.24Zm-1,5.48a4.69,4.69,0,0,0,.69-2.54,3.65,3.65,0,0,0-.8-2.45,2.72,2.72,0,0,0-2.17-.92,2.8,2.8,0,0,0-2.18.92,3.87,3.87,0,0,0-.84,2.7,4,4,0,0,0,.72,2.42,2.74,2.74,0,0,0,2.35,1A2.42,2.42,0,0,0,82.88,34.72Z"
                      />
                      <path
                        className="text"
                        d="M85.66,28h3.85a2.62,2.62,0,0,1,1.85.64,2.34,2.34,0,0,1,.7,1.82,2.66,2.66,0,0,1-.63,1.76,2.33,2.33,0,0,1-1.91.74H86.83v3.64H85.66Zm4.52,1.18A2.48,2.48,0,0,0,89.13,29h-2.3v3h2.3a2.24,2.24,0,0,0,1.27-.33,1.35,1.35,0,0,0,.48-1.18A1.3,1.3,0,0,0,90.18,29.19Z"
                      />
                    </svg>
                    <svg
                      className="button--stopImageMobile"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 128 64.64"
                    >
                      <rect
                        className="background"
                        x="2"
                        y="2"
                        width="124"
                        height="60.64"
                      />
                      <path
                        className="border"
                        d="M124,4V60.64H4V4H124m4-4H0V64.64H128V0Z"
                      />
                      <path
                        className="text"
                        d="M50.63,33.84A2.28,2.28,0,0,0,51,35a2.15,2.15,0,0,0,2,.86,3.27,3.27,0,0,0,1.15-.19,1.32,1.32,0,0,0,1-1.3,1.15,1.15,0,0,0-.43-1,4,4,0,0,0-1.35-.51l-1.13-.26a5.18,5.18,0,0,1-1.57-.57,1.82,1.82,0,0,1-.79-1.61,2.52,2.52,0,0,1,.78-1.91,3.11,3.11,0,0,1,2.21-.74,3.8,3.8,0,0,1,2.24.65A2.4,2.4,0,0,1,56,30.53H54.88a2.31,2.31,0,0,0-.36-1.06,2.06,2.06,0,0,0-1.74-.67,2,2,0,0,0-1.42.43,1.37,1.37,0,0,0-.43,1,1,1,0,0,0,.51.92,7.14,7.14,0,0,0,1.51.47l1.17.28a3.49,3.49,0,0,1,1.31.54,2.06,2.06,0,0,1,.79,1.75,2.21,2.21,0,0,1-1,2,4.46,4.46,0,0,1-2.36.61A3.46,3.46,0,0,1,50.4,36a2.79,2.79,0,0,1-.87-2.2Z"
                      />
                      <path
                        className="text"
                        d="M63.53,28v1h-2.9v7.58H59.45V29h-2.9V28Z"
                      />
                      <path
                        className="text"
                        d="M70.88,29.24a4.73,4.73,0,0,1,.85,2.92,5.06,5.06,0,0,1-1,3.2,4.34,4.34,0,0,1-6.39.19,4.93,4.93,0,0,1-1-3.2,5.09,5.09,0,0,1,.87-3,3.76,3.76,0,0,1,3.29-1.59A3.94,3.94,0,0,1,70.88,29.24Zm-1,5.48a4.67,4.67,0,0,0,.69-2.54,3.6,3.6,0,0,0-.8-2.44,2.7,2.7,0,0,0-2.17-.93,2.81,2.81,0,0,0-2.18.92,3.87,3.87,0,0,0-.84,2.7,4,4,0,0,0,.72,2.42,2.75,2.75,0,0,0,2.35,1A2.42,2.42,0,0,0,69.85,34.72Z"
                      />
                      <path
                        className="text"
                        d="M72.63,28h3.86a2.61,2.61,0,0,1,1.84.64,2.36,2.36,0,0,1,.7,1.82,2.7,2.7,0,0,1-.62,1.76,2.36,2.36,0,0,1-1.92.74H73.8v3.64H72.63Zm4.53,1.18A2.56,2.56,0,0,0,76.1,29H73.8v3h2.3a2.24,2.24,0,0,0,1.27-.33,1.35,1.35,0,0,0,.48-1.18A1.31,1.31,0,0,0,77.16,29.19Z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="tempo">
              <Knob
                value={bpm}
                min={MIN_BPM}
                max={MAX_BPM}
                step={0.01}
                classNameSuffix="tempo"
                labelText="Tempo"
                labelImgUrl={knobLabelTempo}
                knobImgUrl={knobForegroundTempo}
                knobBackgroundImgUrl={knobBackground}
                onChange={onSetBpm}
              />
            </div>
            <div className="shuffle">
              <Knob
                value={state.shuffle}
                min={-0.9}
                max={0.9}
                step={0.01}
                classNameSuffix="shuffle"
                labelText="Shuffle"
                labelImgUrl={knobLabelShuffle}
                knobImgUrl={knobForegroundShuffle}
                knobBackgroundImgUrl={knobBackground}
                onChange={(val) => onSetShuffle(val)}
              />
            </div>
            <div className="clear">
              <div className="switch switch--clear">
                <button
                  className="button button--clear"
                  onClick={() => {
                    onClearHits();
                    sfx.triggerTR808Clear();
                  }}
                >
                  <span className="sr-only">Clear</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 81 57.51"
                  >
                    <g className="textGroup">
                      <path
                        className="text"
                        d="M21.57,5.84a3.56,3.56,0,0,1,2.66-1.06,3.54,3.54,0,0,1,3.15,1.43A3,3,0,0,1,28,7.82h-1.8a2.5,2.5,0,0,0-.44-.94,1.78,1.78,0,0,0-1.43-.56,1.74,1.74,0,0,0-1.52.79,3.82,3.82,0,0,0-.56,2.25,3.33,3.33,0,0,0,.59,2.17,1.82,1.82,0,0,0,1.49.73,1.72,1.72,0,0,0,1.42-.62,3,3,0,0,0,.44-1H28a3.89,3.89,0,0,1-1.19,2.29,3.46,3.46,0,0,1-2.44.89,3.66,3.66,0,0,1-2.9-1.2,4.79,4.79,0,0,1-1.06-3.29A4.79,4.79,0,0,1,21.57,5.84Z"
                      />
                      <path
                        className="text"
                        d="M29.41,4.94h1.81V12h4.31v1.56H29.41Z"
                      />
                      <path
                        className="text"
                        d="M43.13,6.47H38.56V8.3h4.2V9.8h-4.2V12h4.78v1.56H36.8V4.94h6.33Z"
                      />
                      <path
                        className="text"
                        d="M47.22,4.94h2l3.06,8.64h-2l-.57-1.78H46.61L46,13.58H44.13Zm-.12,5.37h2.21l-1.09-3.4Z"
                      />
                      <path
                        className="text"
                        d="M59,5.16a2.12,2.12,0,0,1,.81.61,2.65,2.65,0,0,1,.43.72,2.38,2.38,0,0,1,.16.91A2.56,2.56,0,0,1,60,8.61a1.72,1.72,0,0,1-1,.84,1.62,1.62,0,0,1,.84.68,2.84,2.84,0,0,1,.25,1.36v.58a4.46,4.46,0,0,0,0,.8.75.75,0,0,0,.33.49v.22h-2l-.12-.47a4.53,4.53,0,0,1-.07-.74v-.8a1.65,1.65,0,0,0-.29-1.1,1.43,1.43,0,0,0-1-.28H55.22v3.39H53.46V4.94h4.13A3.88,3.88,0,0,1,59,5.16ZM55.22,6.44V8.76h1.94A2.12,2.12,0,0,0,58,8.62a1,1,0,0,0,.51-1,1.09,1.09,0,0,0-.49-1.06,1.85,1.85,0,0,0-.84-.15Z"
                      />
                      <rect
                        className="border"
                        x="0.5"
                        y="0.5"
                        width="80"
                        height="18"
                      />
                    </g>
                    <path
                      className="circle"
                      d="M40.5,57.51a12,12,0,1,1,12-12A12,12,0,0,1,40.5,57.51Z"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="fill">
              <div className="switch switch--fill">
                <button
                  className="button button--fill button--yellow"
                  onClick={() => {
                    onTriggerFill();
                    sfx.triggerTR808FillIn();
                  }}
                >
                  <span className="sr-only">Fill</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 64.64 64.64"
                  >
                    <rect
                      className="background"
                      x="2"
                      y="2"
                      width="60.64"
                      height="60.64"
                    />
                    <path
                      className="border"
                      d="M60.64,4V60.64H4V4H60.64m4-4H0V64.64H64.64V0Z"
                    />
                    <path
                      className="text"
                      d="M22.46,27.72h6v1.05H23.62v2.61h4.23v1H23.62v3.91H22.46Z"
                    />
                    <path className="text" d="M29.34,27.72h1.18v8.6H29.34Z" />
                    <path
                      className="text"
                      d="M31.81,27.72H33V35.3h4.31v1H31.81Z"
                    />
                    <path
                      className="text"
                      d="M37.88,27.72h1.17V35.3h4.32v1H37.88Z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        <button
          className="button button--deviceSwitcher"
          onClick={onSwitchDevice}
        >
          <img src={buttonToggle} alt="TB-303" />
        </button>
        {isKeyboardControlsActive && (
          <EventListener
            target={document}
            onKeyDown={(evt) => {
              if (evt.key === " ") {
                if (state.playing) {
                  onStop();
                } else {
                  onStart();
                }
                sfx.triggerTR808StartStop();
              }
            }}
          />
        )}
      </div>
    );
  }
);
